<template>
  <div v-if="submitted" class="questionnaire-success-page">
    <div class="questionnaire-success-content">
      Questionnaire submitted successfully
    </div>
  </div>
  <div v-else class="questionnaire-form">
    <!-- Question Items -->
    <div v-for="question in currentPageQuestions" :key="question.id" class="question-item">
      <div class="question-row">
        <div class="question-number-col">
          <span v-if="isRequired(question)" class="required-asterisk">*</span>
          <span class="question-number-text">{{ getQuestionNumber(question) }} →</span>
        </div>
        <div class="question-content-col">
          <div class="question-title-row">
            <span class="question-title">{{ question.title || 'Untitled Question' }}</span>
          </div>

          <div v-if="question.description" class="question-description">
            {{ question.description }}
          </div>

          <!-- Text Type -->
          <div v-if="question.question_type === 'text'" class="question-input">
            <el-input v-model="answers[question.id]" type="textarea" :rows="4"
              :placeholder="'please input'" :required="isRequired(question)"></el-input>
          </div>

          <!-- Rating Type -->
          <div v-if="question.question_type === 'rating'" class="question-rating">
            <div class="rating-stars" role="radiogroup">
              <button
                v-for="star in (question.max_rating || 5)"
                :key="star"
                type="button"
                class="rating-star"
                role="radio"
                :aria-checked="star === answers[question.id]"
                @mouseenter="setRatingHover(question.id, star)"
                @mouseleave="clearRatingHover(question.id)"
                @focus="setRatingHover(question.id, star)"
                @blur="clearRatingHover(question.id)"
                @click="setRatingValue(question.id, star)"
              >
                <img
                  :src="star <= getRatingDisplayValue(question.id) ? iconRateFull : iconRate"
                  alt=""
                >
              </button>
            </div>
          </div>

          <!-- Single Choice Type -->
          <div v-if="question.question_type === 'single'" class="question-single">
            <el-radio-group v-model="answers[question.id]">
              <el-radio v-for="(option, optIndex) in question.options" :key="optIndex" :label="option"
                class="radio-option">
                {{ option }}
              </el-radio>
            </el-radio-group>
          </div>

          <!-- Multi Choice Type -->
          <div v-if="question.question_type === 'multi'" class="question-multi">
            <el-checkbox-group v-model="answers[question.id]">
              <el-checkbox v-for="(option, optIndex) in question.options" :key="optIndex" :label="option"
                class="checkbox-option">
                {{ option }}
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <!-- Matrix Type -->
          <div v-if="question.question_type === 'matrix'" class="question-matrix">
            <div v-for="(option, optIndex) in question.options" :key="optIndex" class="matrix-item">
              <div class="matrix-label">{{ typeof option === 'object' ? option.label : option }}</div>
              <div class="matrix-rating" role="radiogroup">
                <button
                  v-for="star in (question.max_rating || 5)"
                  :key="star"
                  type="button"
                  class="matrix-star"
                  role="radio"
                  :aria-checked="star === answers[question.id][optIndex]"
                  @mouseenter="setMatrixHover(question.id, optIndex, star)"
                  @mouseleave="clearMatrixHover(question.id, optIndex)"
                  @focus="setMatrixHover(question.id, optIndex, star)"
                  @blur="clearMatrixHover(question.id, optIndex)"
                  @click="setMatrixRating(question.id, optIndex, star)"
                >
                  <img
                    :src="star <= getMatrixDisplayRate(question.id, optIndex) ? iconRateFull : iconRate"
                    alt=""
                  >
                </button>
              </div>
            </div>
          </div>

          <!-- Media Type -->
          <div v-if="question.question_type === 'media'" class="question-media">
            <el-upload v-model:file-list="mediaFiles[question.id]" :multiple="true" :limit="10"
              list-type="picture-card" :on-preview="handlePictureCardPreview"
              :on-remove="(file, fileList) => handleRemove(question.id, file, fileList)"
              :before-upload="beforeUpload" :http-request="(options) => handleCustomUpload(options, question.id)">
              <img class="question-media__icon" :src="iconPhoto" alt="">
            </el-upload>
            <el-dialog v-model="dialogVisible">
              <img w-full :src="dialogImageUrl" alt="Preview Image" />
            </el-dialog>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="questionnaire-navigation">
      <div class="questionnaire-navigation-inner">
        <el-button class="back-button" @click="goToPreviousPage">
          <el-icon>
            <ArrowLeft />
          </el-icon>
          Back
        </el-button>
        <el-button type="primary" class="submit-button" :disabled="!canProceed" @click="handleNext">
          {{ isLastPage ? 'Submit' : 'Next' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'
import iconRate from '@/svg/icon-rate.svg'
import iconRateFull from '@/svg/icon-rate-full.svg'
import iconPhoto from '@/svg/icon-photo.svg'
import { getQuestionnaire, submitQuestionnaire, getUploadUrl } from '@/api/index'

export default {
  name: 'QuestionnaireForm',
  emits: ['back-to-review'],
  components: {
    ArrowLeft
  },
  props: {
    questionId: {
      type: String,
      required: true
    },
    linkToken: {
      type: String,
      default: null
    },
    questionnaireData: {
      type: Object,
      default: null
    },
    skipCount: {
      type: Number,
      default: 0
    },
    firstPageAnswers: {
      type: Object,
      default: () => ({})
    },
    firstPageMediaUrls: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const questionnaire = ref(null)
    const fullQuestions = ref([])
    const questions = ref([])
    const answers = reactive({})
    const matrixHoverRates = reactive({})
    const ratingHoverValues = reactive({})
    const mediaFiles = reactive({})
    const mediaPublicUrls = reactive({})
    const currentPage = ref(1)
    const questionsPerPage = ref(5)
    const loading = ref(false)
    const dialogVisible = ref(false)
    const dialogImageUrl = ref('')
    const uploadAction = ref('#') // 需要配置实际上传地址
    const submitted = ref(false)

    // Product info
    const productImage = ref(null)
    const productDescription = ref(null)

    // Initialize answers structure
    const initializeAnswers = () => {
      questions.value.forEach(question => {
        if (question.question_type === 'multi') {
          answers[question.id] = []
        } else if (question.question_type === 'matrix') {
          // Handle both object array (with label/rate) and string array
          if (Array.isArray(question.options)) {
            answers[question.id] = question.options.map((option) => {
              // If option is an object with rate property, use it as default, otherwise 0
              return typeof option === 'object' && option !== null && 'rate' in option 
                ? (Number(option.rate) || 0) 
                : 0
            })
          } else {
            answers[question.id] = []
          }
        } else if (question.question_type === 'media') {
          answers[question.id] = []
          mediaFiles[question.id] = []
          mediaPublicUrls[question.id] = []
        } else {
          answers[question.id] = null
        }
      })
    }

    const isRequired = (question) => Number(question.required) === 1

    const ensureRatingHover = (questionId) => {
      if (typeof ratingHoverValues[questionId] !== 'number') {
        ratingHoverValues[questionId] = 0
      }
    }

    const getRatingDisplayValue = (questionId) => {
      const hover = ratingHoverValues[questionId] || 0
      const value = answers[questionId] || 0
      return hover || value
    }

    const setRatingHover = (questionId, value) => {
      ensureRatingHover(questionId)
      ratingHoverValues[questionId] = value
    }

    const clearRatingHover = (questionId) => {
      ensureRatingHover(questionId)
      ratingHoverValues[questionId] = 0
    }

    const setRatingValue = (questionId, value) => {
      answers[questionId] = value
    }

    const ensureMatrixHover = (questionId, optIndex) => {
      if (!Array.isArray(matrixHoverRates[questionId])) {
        matrixHoverRates[questionId] = []
      }
      if (typeof matrixHoverRates[questionId][optIndex] !== 'number') {
        matrixHoverRates[questionId][optIndex] = 0
      }
    }

    const getMatrixDisplayRate = (questionId, optIndex) => {
      const hover = matrixHoverRates[questionId]?.[optIndex] || 0
      const value = answers[questionId]?.[optIndex] || 0
      return hover || value
    }

    const setMatrixHover = (questionId, optIndex, value) => {
      ensureMatrixHover(questionId, optIndex)
      matrixHoverRates[questionId][optIndex] = value
    }

    const clearMatrixHover = (questionId, optIndex) => {
      ensureMatrixHover(questionId, optIndex)
      matrixHoverRates[questionId][optIndex] = 0
    }

    const setMatrixRating = (questionId, optIndex, value) => {
      if (!Array.isArray(answers[questionId])) {
        answers[questionId] = []
      }
      answers[questionId][optIndex] = value
    }

    // Get question number based on visible order with skipped offset
    const getQuestionNumber = (question) => {
      const index = questions.value.indexOf(question)
      return props.skipCount + index + 1
    }

    // Get current page questions
    const currentPageQuestions = computed(() => {
      const start = (currentPage.value - 1) * questionsPerPage.value
      const end = start + questionsPerPage.value
      return questions.value.slice(start, end)
    })

    // Check if can proceed to next page
    const canProceed = computed(() => {
      return currentPageQuestions.value.every(question => {
        if (!isRequired(question)) return true

        const answer = answers[question.id]
        if (question.question_type === 'multi') {
          return Array.isArray(answer) && answer.length > 0
        } else if (question.question_type === 'matrix') {
          return Array.isArray(answer) && answer.every(rating => rating > 0)
        } else if (question.question_type === 'media') {
          return Array.isArray(answers[question.id]) && answers[question.id].length > 0
        }
        return answer !== null && answer !== undefined && answer !== ''
      })
    })

    // Check if is last page
    const isLastPage = computed(() => {
      const totalPages = Math.ceil(questions.value.length / questionsPerPage.value)
      return currentPage.value >= totalPages
    })

    const applyQuestionnairePayload = (payload) => {
      if (!payload) {
        console.warn('No data in response')
        return
      }

      questionnaire.value = payload
      const loadedQuestions = payload.questions || []

      // Sort questions by sort_order, keep original order when tied
      const sortedQuestions = loadedQuestions
        .map((question, index) => ({ question, index }))
        .sort((a, b) => {
          const orderA = a.question.sort_order || 0
          const orderB = b.question.sort_order || 0
          if (orderA !== orderB) {
            return orderA - orderB
          }
          return a.index - b.index
        })
        .map(item => item.question)

      fullQuestions.value = sortedQuestions
      questions.value = sortedQuestions.slice(props.skipCount)
      questionsPerPage.value = questions.value.length || 1

      // Set product info
      if (payload.product_image) {
        productImage.value = payload.product_image
      }
      if (payload.product_description) {
        productDescription.value = payload.product_description
      }

      initializeAnswers()
    }

    // Load questionnaire data
    const loadQuestionnaire = async () => {
      loading.value = true
      try {
        const response = await getQuestionnaire(props.questionId)
        const payload = response.data?.data || response.data
        applyQuestionnairePayload(payload)
      } catch (error) {
        console.error('Error loading questionnaire:', error)
        ElMessage.error('Failed to load questionnaire')
      } finally {
        loading.value = false
      }
    }

    // Handle next button
    const handleNext = () => {
      if (!canProceed.value) {
        ElMessage.warning('Please complete all required questions')
        return
      }

      if (isLastPage.value) {
        handleSubmit()
      } else {
        goToNextPage()
      }
    }

    // Go to next page
    const goToNextPage = () => {
      currentPage.value++
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    // Go to previous page
    const goToPreviousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      emit('back-to-review')
    }

    // Submit questionnaire
    const handleSubmit = async () => {
      // 从 URL 获取 linkToken
      let urlToken = props.linkToken || null
      try {
        const urlParams = new URLSearchParams(window.location.search)
        urlToken = urlParams.get('link_token') || urlToken
      } catch (e) {
        console.error('Error extracting link_token from URL', e)
      }

      const allQuestions = fullQuestions.value.length
        ? fullQuestions.value
        : questions.value

      const textQueue = [
        props.firstPageAnswers?.text || '',
        props.firstPageAnswers?.title || ''
      ]
      let textIndex = 0

      // Format answers according to API structure
      const formattedAnswers = allQuestions.map((question, index) => {
        // Skip matrix type questions, they will be handled separately
        if (question.question_type === 'matrix') {
          return null
        }

        const answerData = {
          question_id: question.id,
          answer_text: null,
          answer_options: [],
          answer_rating: 0,
          media_urls: []
        }

        if (index < props.skipCount) {
          // First page answers
          switch (question.question_type) {
            case 'text': {
              const textValue = textQueue[textIndex] || ''
              textIndex += 1
              answerData.answer_text = textValue
              break
            }
            case 'rating':
              answerData.answer_rating = props.firstPageAnswers?.rating || 0
              break
            case 'media':
              answerData.media_urls = Array.isArray(props.firstPageMediaUrls)
                ? props.firstPageMediaUrls
                : []
              break
            default:
              break
          }
        } else {
          // Second page answers
          const answer = answers[question.id]
          const mediaUrls = Array.isArray(answers[question.id])
            ? answers[question.id]
            : []

          switch (question.question_type) {
            case 'text':
              answerData.answer_text = answer || ''
              break
            case 'rating':
              answerData.answer_rating = answer || 0
              break
            case 'single':
              answerData.answer_options = answer ? [answer] : []
              break
            case 'multi':
              answerData.answer_options = Array.isArray(answer) ? answer : []
              break
            case 'media':
              answerData.media_urls = mediaUrls
              break
          }
        }

        return answerData
      }).filter(answer => {
        // Filter out null (matrix questions) and empty answers
        if (!answer) return false
        return answer.answer_text ||
          answer.answer_options.length > 0 ||
          answer.answer_rating > 0 ||
          answer.media_urls.length > 0
      })

      // Handle matrix type questions separately
      // Matrix questions should be submitted as a single record with answer_options containing all {label, rate} objects
      allQuestions.forEach((question) => {
        if (question.question_type === 'matrix' && Array.isArray(question.options)) {
          const matrixAnswers = answers[question.id]
          if (!matrixAnswers) return

          // Build answer_options array with {label, rate} objects
          const answerOptions = question.options.map((option, index) => {
            // Get label from option (object) or use option directly (string)
            const label = typeof option === 'object' && option !== null && 'label' in option
              ? option.label
              : (option || String(option))
            
            // Get rate from user's answer (matrixAnswers[index])
            const rate = matrixAnswers[index] || 0

            return {
              label: label,
              rate: rate
            }
          })

          // Only add if at least one option has a rating > 0
          const hasAnyRating = answerOptions.some(opt => opt.rate > 0)
          if (hasAnyRating) {
            formattedAnswers.push({
              question_id: question.id,
              answer_text: null,
              answer_options: answerOptions,
              answer_rating: 0,
              media_urls: []
            })
          }
        }
      })

      loading.value = true
      try {
        const res = await submitQuestionnaire(urlToken, formattedAnswers);
        console.log('res', res)
        if (res.code === 200) {
          ElMessage.success('Questionnaire submitted successfully!')
          submitted.value = true
        } else {
          ElMessage.error(`${ res.message } : Failed to submit questionnaire`)
        }
        console.log('Questionnaire submitted:', formattedAnswers)
      } catch (error) {
        console.error('Error submitting questionnaire:', error)
        ElMessage.error(error.response?.data?.message || 'Failed to submit questionnaire')
      } finally {
        loading.value = false
      }
    }

    // Handle media upload
    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url
      dialogVisible.value = true
    }

    const handleRemove = (questionId, file) => {
      if (!questionId) return
      const url = file?.response?.public_url || file?.response?.url || file?.url
      if (!url) return
      if (Array.isArray(mediaPublicUrls[questionId])) {
        mediaPublicUrls[questionId] = mediaPublicUrls[questionId].filter(item => item !== url)
      }
      if (Array.isArray(answers[questionId])) {
        answers[questionId] = answers[questionId].filter(item => item !== url)
      }
    }

    const beforeUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isValid = isImage || isVideo
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isValid) {
        ElMessage.error('Only image and video files are allowed!')
        return false
      }
      if (!isLt10M) {
        ElMessage.error('File size must be smaller than 10MB!')
        return false
      }
      return true
    }

    // Custom upload handler for media files
    const handleCustomUpload = async (options, questionId) => {
      const { file } = options
      
      try {
        // Get linkToken from props or URL
        let urlToken = props.linkToken || null
        if (!urlToken) {
          try {
            const urlParams = new URLSearchParams(window.location.search)
            urlToken = urlParams.get('link_token')
          } catch (e) {
            console.error('Error extracting link_token from URL', e)
          }
        }
        
        if (!urlToken) {
          ElMessage.error('Link token not found')
          options.onError(new Error('Link token not found'))
          return
        }

        // Get upload URL
        const uploadUrlResponse = await getUploadUrl(
          urlToken,
          questionId,
          file.name,
          file.type,
          file.size
        )
        
        const uploadData = uploadUrlResponse.data?.data || uploadUrlResponse.data
        if (!uploadData || !uploadData.upload_url) {
          ElMessage.error('Failed to get upload URL')
          options.onError(new Error('Failed to get upload URL'))
          return
        }

        // Upload file to OSS using upload_url
        const uploadResponse = await fetch(uploadData.upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type
          },
          body: file
        })

        if (!uploadResponse.ok) {
          ElMessage.error('Failed to upload file')
          options.onError(new Error('Failed to upload file'))
          return
        }

        // Update file object with public_url for submission
        file.url = uploadData.public_url
        file.response = { url: uploadData.public_url, public_url: uploadData.public_url }
        if (!Array.isArray(mediaPublicUrls[questionId])) {
          mediaPublicUrls[questionId] = []
        }
        if (uploadData.public_url) {
          mediaPublicUrls[questionId].push(uploadData.public_url)
          if (Array.isArray(answers[questionId])) {
            answers[questionId].push(uploadData.public_url)
          } else {
            answers[questionId] = [uploadData.public_url]
          }
        }
        options.onSuccess({ url: uploadData.public_url, public_url: uploadData.public_url }, file)
      } catch (error) {
        console.error('Error uploading file:', error)
        ElMessage.error('Failed to upload file: ' + (error.message || 'Unknown error'))
        options.onError(error)
      }
    }

    onMounted(() => {
      if (props.questionnaireData) {
        applyQuestionnairePayload(props.questionnaireData)
        return
      }
      loadQuestionnaire()
    })

    return {
      questionnaire,
      questions,
      answers,
      mediaFiles,
      currentPage,
      currentPageQuestions,
      canProceed,
      isLastPage,
      loading,
      dialogVisible,
      dialogImageUrl,
      uploadAction,
      productImage,
      productDescription,
      iconPhoto,
      iconRate,
      iconRateFull,
      isRequired,
      getRatingDisplayValue,
      setRatingHover,
      clearRatingHover,
      setRatingValue,
      getMatrixDisplayRate,
      setMatrixHover,
      clearMatrixHover,
      setMatrixRating,
      getQuestionNumber,
      handleNext,
      goToNextPage,
      goToPreviousPage,
      handleSubmit,
      handlePictureCardPreview,
      handleRemove,
      beforeUpload,
      handleCustomUpload,
      submitted
    }
  }
}
</script>

<style scoped>
.questionnaire-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
}

.questionnaire-success-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  text-align: center;
}

.questionnaire-success-content {
  font-size: 18px;
  font-weight: 600;
  color: #0F1111;
}

.question-item {
  margin-bottom: 0px;
  padding: 10px 20px;
  background-color: #fff;
  border-radius: 4px;
  width: 832px;
}

.required-asterisk {
  color: #d13212;
  margin-right: 2px;
}

.question-row {
  display: grid;
  grid-template-columns: 56px 1fr;
  column-gap: 12px;
  align-items: start;
}

.question-number-col {
  text-align: right;
  padding-top: 2px;
}

.question-number-text {
  font-weight: 700;
  color: #0F1111;
  white-space: nowrap;
}

.question-content-col {
  flex: 1;
  width: 784px;
  max-width: 784px;
}

.question-title-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
}

.question-title {
  font-size: 16px;
  font-weight: 400;
  color: #0F1111;
  line-height: 1.5;
}

.question-description {
  font-size: 14px;
  color: #565959;
  margin-bottom: 15px;
  line-height: 1.5;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
}

/* Product Image Container */
.product-image-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  gap: 15px;
}

.product-image {
  width: 64px;
  height: 64px;
  object-fit: contain;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
}

.product-title {
  font-size: 14px;
  color: #565959;
  line-height: 1.4;
}

/* Question Input Styles */
.question-input {
  margin-top: 15px;
}

.question-rating {
  margin-top: 15px;
}

/* Rating star size */
.rating-stars {
  display: inline-flex;
  gap: 4px;
}

.rating-star {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
}

.rating-star img {
  width: 36px;
  height: 36px;
  display: block;
}

.rating-star:focus-visible {
  outline: 2px solid #e77600;
  outline-offset: 2px;
}

.question-single {
  margin-top: 15px;
  width: 100%;
}

.radio-option {
  display: block;
  margin-bottom: 12px;
  padding: 0 12px;
  border: 1px solid #BDBEC0;
  border-radius: 8px;
  background: #F6FAFB;
  width: 784px;
  height: 32px;
  line-height: 32px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.radio-option:hover {
  background-color: #f7f7f7;
}

/* Ensure each radio option is on its own line and aligned */
.question-single :deep(.el-radio) {
  display: flex;
  align-items: center;
  width: 784px;;
  margin-right: 0;
}

.question-single :deep(.el-radio__label) {
  flex: 1;
  line-height: 1.4;
}

.question-multi {
  margin-top: 15px;
  width: 100%;
}

.checkbox-option {
  display: block;
  margin-bottom: 12px;
  padding: 0 12px;
  border: 1px solid #BDBEC0;
  border-radius: 8px;
  background: #F6FAFB;
  width: 100%;
  height: 32px;
  line-height: 32px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.checkbox-option:hover {
  background-color: #f7f7f7;
}

/* Ensure each checkbox option is on its own line and aligned */
.question-multi :deep(.el-checkbox) {
  display: flex;
  align-items: center;
  width: 100%;
  margin-right: 0;
}

.question-multi :deep(.el-checkbox__label) {
  flex: 1;
  line-height: 1.4;
}

/* Matrix Type */
.question-matrix {
  margin-top: 15px;
}

.matrix-item {
  display: flex;
  align-items: center;
}

.matrix-rating {
  display: inline-flex;
  gap: 4px;
}

.matrix-star {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
}

.matrix-star img {
  width: 36px;
  height: 36px;
  display: block;
}

.matrix-star:focus-visible {
  outline: 2px solid #e77600;
  outline-offset: 2px;
}

.matrix-label {
  min-width: 200px;
  font-size: 14px;
  color: #0F1111;
  margin-right: 20px;
}

/* Media Upload */
.question-media {
  margin-top: 15px;
}

/* Navigation */
.questionnaire-navigation {
  padding: 16px 0;
  border-top: 1px solid #d5d9d9;
  margin-top: 24px;
  position: absolute;
  left: 0;
  width: 100%;
  background-color: #fff;
}

.questionnaire-navigation-inner {
  width: 832px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 138px;
  gap: 575px;
}

/* Media upload width */
.question-media :deep(.el-upload--picture-card) {
  width: 784px;
  height: 64px;
  background: #F6FAFB;
  border: 1px dashed #BDBEC0;
  border-radius: 4px;
}

.question-media__icon {
  width: 28px;
  height: 28px;
  display: block;
}

.back-button {
  background-color: #f0f2f2;
  color: #0F1111;
  border: 1px solid #d5d9d9;
  padding: 8px 18px;
  border-radius: 999px;
  min-width: 96px;
}

.back-button:hover {
  background-color: #e7e7e7;
}

.submit-button {
  background: #FFD713 !important;
  border: none;
  padding: 8px 26px;
  font-family: PingFang SC-Medium;
  font-weight: 400;
  color: #191725;
  border-radius: 999px;
  min-width: 110px;
}

.submit-button:hover:not(:disabled) {
  background: #f7cf00 !important;
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .questionnaire-form {
    padding: 15px;
  }

  .matrix-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .matrix-label {
    min-width: auto;
    margin-bottom: 10px;
    margin-right: 0;
  }
}
</style>
